import type { ButtonColor, ButtonSize, ButtonVariant } from '@/components/primitives/button/Button';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

const colors: Record<ButtonColor, Record<ButtonVariant, string>> = {
  black: {
    solid: clsx(
      'bg-content-primary border-content-primary text-content-inverted',
      'hover:bg-interactive-hover hover:border-interactive-hover hover:text-content-inverted',
      'focus-visible:bg-interactive-hover focus-visible:border-interactive-hover focus-visible:text-content-inverted',
      'active:bg-interactive-active active:border-interactive-active active:text-content-inverted',
      'disabled:bg-interactive-disabled disabled:border-interactive-disabled disabled:text-content-inverted',
    ),
    normal: clsx(
      'bg-surface-sunken border-surface-sunken text-content-primary',
      'hover:bg-overlay-hover hover:border-overlay-hover hover:text-content-primary',
      'focus-visible:bg-overlay-hover focus-visible:border-overlay-hover focus-visible:text-content-primary',
      'active:bg-interactive-active-subtle active:border-interactive-active-subtle active:text-content-primary',
      'disabled:bg-surface-sunken disabled:border-surface-sunken disabled:text-content-tertiary',
    ),
    outline: clsx(
      'bg-transparent border-default text-content-primary',
      'hover:border-strong hover:text-content-primary',
      'focus-visible:bg-surface-sunken focus-visible:border-strong focus-visible:text-content-primary',
      'active:border-strong active:text-content-primary',
      'disabled:bg-surface-sunken disabled:border-subtle disabled:text-content-tertiary',
    ),
    ghost: clsx(
      'bg-transparent border-transparent text-content-primary',
      'hover:bg-overlay-hover hover:border-overlay-hover hover:text-content-primary',
      'focus-visible:bg-surface-sunken focus-visible:text-content-primary',
      'active:bg-overlay-hover active:text-content-primary',
      'disabled:bg-transparent disabled:text-content-tertiary',
    ),
  },
  // Red variants use dark: prefixes because opacity modifiers (e.g. red-500/20)
  // can't be expressed as semantic CSS custom properties
  red: {
    solid: clsx(
      'bg-red-500 border-red-500 text-gray-50',
      'hover:bg-red-300 hover:border-red-300 hover:text-gray-50',
      'focus-visible:bg-red-300 focus-visible:border-red-300 focus-visible:text-gray-50',
      'active:bg-red-400 active:border-red-400 active:text-gray-50',
      'disabled:bg-red-100 disabled:border-red-100 disabled:text-gray-50',
      'dark:disabled:bg-red-500/20 dark:disabled:border-red-500/20 dark:disabled:text-gray-500',
    ),
    normal: clsx(
      'bg-red-50 border-red-50 text-red-500',
      'hover:bg-red-100 hover:border-red-100 hover:text-red-500',
      'focus-visible:bg-red-100 focus-visible:border-red-100 focus-visible:text-red-500',
      'active:bg-red-200 active:border-red-200 active:text-red-500',
      'disabled:bg-red-50 disabled:border-red-50 disabled:text-red-200',
      'dark:bg-red-500/10 dark:border-red-500/10',
      'dark:hover:bg-red-500/20 dark:hover:border-red-500/20',
      'dark:focus-visible:bg-red-500/20 dark:focus-visible:border-red-500/20',
      'dark:active:bg-red-500/30 dark:active:border-red-500/30',
      'dark:disabled:bg-red-500/5 dark:disabled:border-red-500/5 dark:disabled:text-red-400/50',
    ),
    outline: clsx(
      'bg-transparent border-red-200 text-red-500',
      'hover:border-red-400 hover:text-red-500',
      'focus-visible:bg-red-50 focus-visible:border-red-400 focus-visible:text-red-500',
      'active:border-red-500 active:text-red-500',
      'disabled:bg-red-50 disabled:border-red-100 disabled:text-red-200',
      'dark:border-red-400/40',
      'dark:focus-visible:bg-red-500/10',
      'dark:disabled:bg-transparent dark:disabled:border-red-500/20 dark:disabled:text-red-400/40',
    ),
    ghost: clsx(
      'bg-transparent border-transparent text-red-500',
      'hover:bg-red-100 hover:border-red-100 hover:text-red-500',
      'focus-visible:bg-red-50 focus-visible:text-red-500',
      'active:bg-red-200 active:text-red-500',
      'disabled:bg-transparent disabled:text-red-200',
      'dark:hover:bg-red-500/10 dark:hover:border-red-500/10',
      'dark:focus-visible:bg-red-500/10',
      'dark:active:bg-red-500/20',
      'dark:disabled:text-red-400/40',
    ),
  },
};

// size directly affects the padding and font size of the button
const sizes: Record<ButtonSize, string> = {
  md: 'px-[14px] py-[5px] text-sm/[22px]',
  sm: 'px-3 py-1 text-xs/[20px]',
  xs: 'px-[10px] py-[3px] text-[10px]/[18px]',
};

/**
 * Returns a class name which can be applied to an element to make it look
 * and behave like a button. This is abstracted separately from the Button
 * component to make it easier to reuse these styles in other places, since
 * that's common for buttons.
 */
export function getButtonClassName({
  color,
  size,
  variant,
}: {
  color: ButtonColor;
  size: ButtonSize;
  variant: ButtonVariant;
}) {
  return twMerge(
    // some baseline styles for all button types
    'border border-solid relative select-none whitespace-nowrap rounded',
    'inline-flex flex-row gap-[6px] items-center justify-center',
    'not:disabled:cursor-pointer',
    // and baseline transitions
    'transition-colors duration-200 ease-in-out',
    // and the size
    sizes[size],
    // and the color
    colors[color][variant],
    // disable focus ring; it will only show on focus-visible
    'focus:outline-none',
  );
}
